/**
 * In-process MCP server that exposes the agent's Conveyor tools to the spawned
 * `claude` CLI over Streamable HTTP on loopback.
 *
 * The tool handlers must run IN the agent process — they call
 * `connection.call(...)` on the live task-token `AgentConnection`. So instead of
 * spawning a separate stdio MCP child and bridging calls back over a socket, we
 * serve the same in-process handlers over an HTTP MCP transport bound to
 * `127.0.0.1`, and point `claude` at it via `--mcp-config` (type: "http"). This
 * is the wiring the PTY harness deferred ("S4"); the SDK harness already gets
 * these tools in-process.
 *
 * Security: bound to loopback only (never 0.0.0.0) and gated by a random
 * per-run bearer token that `claude` echoes from the generated mcp-config.
 */

import { createServer, type Server as HttpServer } from "node:http";
import type { IncomingMessage, ServerResponse } from "node:http";
import { writeFile } from "node:fs/promises";
import { join } from "node:path";
import { randomBytes } from "node:crypto";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
import type { HarnessMcpServer, HarnessToolDefinition } from "../types.js";
import { PtyMcpServer } from "./mcp-server.js";

const LOOPBACK = "127.0.0.1";

export interface PtyToolServerHandle {
  /** `http://127.0.0.1:<port>/mcp` — the URL written into the mcp-config. */
  url: string;
  /** Bearer token required on every request (defense-in-depth over loopback). */
  token: string;
}

export class PtyToolServer {
  private http: HttpServer | null = null;
  private transport: StreamableHTTPServerTransport | null = null;
  private mcp: McpServer | null = null;
  private readonly token = randomBytes(24).toString("base64url");

  constructor(
    private readonly name: string,
    private readonly tools: HarnessToolDefinition[],
  ) {}

  async start(): Promise<PtyToolServerHandle> {
    const mcp = new McpServer({ name: this.name, version: "1.0.0" });
    // The SDK's `tool()` is heavily generic over the zod shape; our tool defs are
    // intentionally loose-typed (`schema: z.ZodRawShape`, handler returns MCP
    // content). Cast the registrar to a simpler shape to bridge the boundary
    // without widening to `any` — the SDK still validates input against `schema`.
    const register = mcp.tool.bind(mcp) as unknown as (
      name: string,
      description: string,
      schema: unknown,
      cb: (args: unknown) => unknown,
    ) => void;
    for (const tool of this.tools) {
      register(tool.name, tool.description, tool.schema, (args) => tool.handler(args));
    }

    const transport = new StreamableHTTPServerTransport({
      sessionIdGenerator: () => randomBytes(16).toString("hex"),
      enableJsonResponse: true,
    });
    await mcp.connect(transport);

    const server = createServer((req, res) => {
      void this.handle(req, res, transport);
    });
    await new Promise<void>((resolve, reject) => {
      server.once("error", reject);
      server.listen(0, LOOPBACK, () => resolve());
    });

    const address = server.address();
    const port = address && typeof address === "object" ? address.port : 0;

    this.http = server;
    this.transport = transport;
    this.mcp = mcp;
    return { url: `http://${LOOPBACK}:${port}/mcp`, token: this.token };
  }

  private async handle(
    req: IncomingMessage,
    res: ServerResponse,
    transport: StreamableHTTPServerTransport,
  ): Promise<void> {
    if (req.headers.authorization !== `Bearer ${this.token}`) {
      res.writeHead(401).end();
      return;
    }
    try {
      await transport.handleRequest(req, res);
    } catch {
      if (res.headersSent) res.end();
      else res.writeHead(500).end();
    }
  }

  async close(): Promise<void> {
    try {
      await this.transport?.close();
    } catch {
      /* already closed */
    }
    this.transport = null;
    try {
      await this.mcp?.close();
    } catch {
      /* already closed */
    }
    this.mcp = null;
    const http = this.http;
    this.http = null;
    if (http) {
      await new Promise<void>((resolve) => {
        http.close(() => resolve());
      });
    }
  }
}

export interface ToolServersResult {
  servers: PtyToolServer[];
  /** Path to the written `--mcp-config`, or null when there were no tools. */
  mcpConfigPath: string | null;
}

/**
 * Start one loopback HTTP MCP server per harness tool group (in practice just
 * "conveyor") and write the `--mcp-config` the spawned `claude` consumes. No-op
 * (null config) when the harness was constructed without tools.
 */
export async function startToolServers(
  mcpServers: Record<string, HarnessMcpServer>,
  tempDir: string,
): Promise<ToolServersResult> {
  const servers: PtyToolServer[] = [];
  const config: Record<string, { type: "http"; url: string; headers: Record<string, string> }> = {};
  for (const [name, handle] of Object.entries(mcpServers)) {
    const tools = handle instanceof PtyMcpServer ? handle.tools : [];
    if (tools.length === 0) continue;
    const server = new PtyToolServer(name, tools);
    const { url, token } = await server.start();
    servers.push(server);
    config[name] = { type: "http", url, headers: { Authorization: `Bearer ${token}` } };
  }
  if (Object.keys(config).length === 0) return { servers, mcpConfigPath: null };
  const mcpConfigPath = join(tempDir, "mcp-config.json");
  await writeFile(mcpConfigPath, JSON.stringify({ mcpServers: config }, null, 2), "utf8");
  return { servers, mcpConfigPath };
}
